import { VALIDATION_ENUM } from "enums";
import { Field, Formik, Form } from "formik";
import { useState } from "react";
import { useSellAssetAdmin, useUploadInvestmentFile } from "hooks/investments";
import { useGetClientsOptions } from "hooks/users";
import { useGetMe } from "hooks/auth";
import {
  Button,
  CalendarInput,
  DropFileInput,
  FullscreenModal,
  Input,
  SelectComponent,
} from "shared";
import { createPortal } from "react-dom";
import * as Yup from "yup";
import "./SellAssetAdminModal.scss";

interface SellAssetAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  hardClose: () => void;
  onSuccess?: () => void;
  item: any | null;
  slug?: string;
}

interface FormValues {
  clientId: string | null;
  price: string;
  soldAt: string;
  invoice: File | null;
}

const SellAssetAdminModal: React.FC<SellAssetAdminModalProps> = ({
  isOpen,
  onClose,
  hardClose,
  onSuccess,
  item,
  slug,
}) => {
  const hidePrice = slug === "investment_policy";
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const { mutateAsync: sellAsset, isPending } = useSellAssetAdmin();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { data: clientOptions } = useGetClientsOptions(isOpen);
  const { data: me } = useGetMe();

  const initialValues: FormValues = {
    clientId: null,
    price: "",
    soldAt: "",
    invoice: null,
  };

  const validationSchema = Yup.object().shape({
    clientId: Yup.mixed().required(VALIDATION_ENUM.REQUIRED),
    price: hidePrice
      ? Yup.number().nullable()
      : Yup.number()
          .typeError(VALIDATION_ENUM.REQUIRED)
          .required(VALIDATION_ENUM.REQUIRED)
          .positive(VALIDATION_ENUM.REQUIRED),
    soldAt: Yup.string().required(VALIDATION_ENUM.REQUIRED),
    invoice: Yup.mixed().nullable(),
  });

  const handleSubmit = async (values: FormValues) => {
    if (!item?.uuid) return;

    let fileUuid: string | null = null;
    if (values.invoice) {
      const userId: string | number = (me as any)?.user?.id ?? (me as any)?.id ?? "";
      try {
        setIsUploadingFile(true);
        const uploaded = await uploadFile({
          file: values.invoice,
          type: "attachment",
          parentId: userId,
        });
        fileUuid = Array.isArray(uploaded) ? uploaded[0]?.uuid : (uploaded as any)?.uuid;
      } finally {
        setIsUploadingFile(false);
      }
    }

    await sellAsset(
      {
        uuid: item.uuid,
        clientId: values.clientId as string,
        price: Number(values.price),
        soldAt: values.soldAt,
        fileUuid,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          hardClose();
        },
      },
    );
  };

  if (!item) return null;

  return createPortal(
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="sell-asset-admin-modal">
        <h2 className="fullscreen-modal__header">
          Sprzedaż aktywa <i>{item?.name}</i>
        </h2>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {(formik) => (
            <Form className="sell-asset-admin-modal__form">
              <div className="sell-asset-admin-modal__fields">
                <div className="sell-asset-admin-modal__field">
                  <Field
                    label="Klient"
                    name="clientId"
                    id="clientId"
                    as={SelectComponent}
                    options={clientOptions}
                    isSearchable
                    error={
                      (formik.touched.clientId || formik.submitCount > 0) &&
                      formik.errors.clientId
                        ? String(formik.errors.clientId)
                        : undefined
                    }
                  />
                </div>

                {!hidePrice && (
                  <div className="sell-asset-admin-modal__field">
                    <Input
                      type="number"
                      name="price"
                      label="Cena sprzedaży"
                      precision={2}
                      maxLength={13}
                      error={
                        (formik.touched.price || formik.submitCount > 0) &&
                        formik.errors.price
                          ? String(formik.errors.price)
                          : undefined
                      }
                    />
                  </div>
                )}

                <div className="sell-asset-admin-modal__field">
                  <Field
                    type="date"
                    name="soldAt"
                    label={hidePrice ? "Data przekazania" : "Data sprzedaży"}
                    component={CalendarInput}
                    errors={formik.errors}
                    yearMonthPicker={false}
                  />
                </div>

                <div className="sell-asset-admin-modal__field">
                  <Field
                    label="Umowa"
                    name="invoice"
                    id="invoice"
                    as={DropFileInput}
                    accept=".png, .jpg, .doc, .docx, .pdf"
                    description="Maks. 5 MB (PDF, DOC, DOCX, JPG, JPEG, PNG)"
                    optional
                  />
                </div>
              </div>

              <div className="sell-asset-admin-modal__actions">
                <Button
                  type="button"
                  onClick={onClose}
                  label="Anuluj"
                  className="button--stroke button--rounded"
                />
                <Button
                  type="submit"
                  onClick={() => {}}
                  label={isPending || isUploadingFile ? "Zapisywanie..." : "Potwierdź"}
                  className="button--rounded button--black"
                  isLoading={isPending || isUploadingFile}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullscreenModal>,
    document.body,
  );
};

export default SellAssetAdminModal;
